<script>
  import { tick, onMount, onDestroy } from "svelte"
  import backend from "~/backend"
  import { crossfade, fly } from "svelte/transition"
  import { cubicOut } from "svelte/easing"
  import { onPaste } from "~/utils"
  import ShoppingListItem from "./ShoppingListItem.svelte"

  export let initialData

  let uuid = 0

  function createEmptyItem() {
    return {
      id: uuid++,
      product: "",
      completed: false,
      completedAt: null
    }
  }

  function withTrailingEmpty(items) {
    return items.some((item) => !item.completed)
      ? items
      : [...items, createEmptyItem()]
  }

  let list = withTrailingEmpty(
    (initialData.list || []).map((el) => ({
      ...el,
      id: uuid++,
      product: el.product || "",
      completed: !!el.completed,
      completedAt: el.completedAt || null
    }))
  )

  const [send, receive] = crossfade({
    duration: 280,
    easing: cubicOut
  })

  const productRefs = new Map()

  function registerProductRef(node, item) {
    productRefs.set(item.id, node)
    return {
      destroy() {
        // Guard against a remount (item moved between active/completed sections)
        // having already replaced this entry.
        if (productRefs.get(item.id) === node) {
          productRefs.delete(item.id)
        }
      }
    }
  }

  async function toggle(item) {
    const currentNode = productRefs.get(item.id)
    const hadFocus = currentNode && document.activeElement === currentNode

    const nowCompleted = !item.completed
    list = withTrailingEmpty(
      list.map((entry) =>
        entry.id === item.id
          ? { ...entry, completed: nowCompleted, completedAt: nowCompleted ? Date.now() : null }
          : entry
      )
    )

    await tick()

    if (hadFocus) {
      productRefs.get(item.id)?.focus()
    }
  }

  async function insertAt(item, offset) {
    const index = list.indexOf(item) + offset
    const newItem = createEmptyItem()
    list = [...list.slice(0, index), newItem, ...list.slice(index)]

    await tick()
    focusItem(newItem)
  }

  function focusItem(item) {
    if (!item) return
    const node = productRefs.get(item.id)
    if (!node) return
    node.focus()

    const selection = window.getSelection()
    if (!selection) return

    const range = document.createRange()
    range.selectNodeContents(node)
    range.collapse(!node.textContent.length)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  function deleteItem(item) {
    const index = list.indexOf(item)
    list = withTrailingEmpty(list.filter((entry) => entry.id !== item.id))
    focusItem(list[Math.max(0, index - 1)])
  }

  function handleBackspace(item, event) {
    if (item.product.length === 0 && list.length > 1) {
      event.preventDefault()
      deleteItem(item)
    }
  }

  function handleProductInput(item, event) {
    const newText = event.target.textContent
    if (item.product === newText) return

    // Mutate in place rather than rebuilding the array: the DOM (contenteditable)
    // is the source of truth here, and replacing the item identity would cause
    // Svelte to rewrite textContent and disturb the caret.
    item.product = newText
    list = list
  }

  function handleKeyDown(item, event) {
    const usingCommand = event.metaKey || event.ctrlKey
    const key = event.key.toLowerCase()

    if (usingCommand && (event.key === "Enter" || key === "d")) {
      event.preventDefault()
      toggle(item)
      return
    }

    if (usingCommand && key === "j") {
      event.preventDefault()
      const next = list[list.indexOf(item) + 1]
      if (next) focusItem(next)
      return
    }

    if (usingCommand && key === "k") {
      event.preventDefault()
      const previous = list[list.indexOf(item) - 1]
      if (previous) focusItem(previous)
      return
    }

    if (event.shiftKey && event.key === "Enter") {
      event.preventDefault()
      insertAt(item, 0)
    } else if (event.key === "Enter") {
      event.preventDefault()
      insertAt(item, 1)
    } else if (event.key === "Backspace") {
      handleBackspace(item, event)
    }
  }

  function clearCompleted() {
    list = withTrailingEmpty(list.filter((item) => !item.completed))
  }

  $: activeItems = list.filter((item) => !item.completed)
  $: completedItems = list
    .filter((item) => item.completed)
    .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))

  function toPayload(currentList) {
    return currentList.map((el) => ({
      product: el.product,
      completed: el.completed,
      completedAt: el.completedAt
    }))
  }

  let saveTimer
  let lastSavedSnapshot = JSON.stringify(toPayload(list))

  function scheduleSave(currentList) {
    const payload = toPayload(currentList)
    const serialized = JSON.stringify(payload)
    if (serialized === lastSavedSnapshot) return
    lastSavedSnapshot = serialized

    clearTimeout(saveTimer)
    saveTimer = setTimeout(
      () => backend.updateShoppingList({ shopping_list: payload }),
      500
    )
  }

  $: scheduleSave(list)

  onMount(() => {
    window.addEventListener("paste", onPaste)
  })

  onDestroy(() => {
    window.removeEventListener("paste", onPaste)
    clearTimeout(saveTimer)
  })
</script>

<main class="mx-auto bg-cream/30">
  <div class="space-y-4">
    <div class="flex flex-col space-y-6">
      <div class="flex-1">
        <div class="pl-3 border-l-4 border-primary space-y-1">
          {#each activeItems as item (item.id)}
            <div
              class="space-y-1 relative"
              in:receive={{ key: item.id }}
              out:send={{ key: item.id }}
            >
              <div transition:fly|local>
                <ShoppingListItem
                  {item}
                  {registerProductRef}
                  onToggle={toggle}
                  onInput={handleProductInput}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
          {/each}
        </div>
      </div>

      {#if completedItems.length > 0}
        <div class="space-y-3 mt-16">
          <div class="flex items-center gap-4">
            <h2 class="text-sm font-semibold uppercase tracking-wide text-fg-muted">Completed</h2>
            <button
              on:click={clearCompleted}
              type="button"
              class="btn btn-outline btn-xs rounded-xl"
            >Clear</button>
          </div>
          <div class="pl-3 border-l-4 border-primary/30 space-y-1">
            {#each completedItems as item (item.id)}
              <div
                class="space-y-1 relative opacity-80"
                in:receive={{ key: item.id }}
                out:send={{ key: item.id }}
              >
                <div transition:fly|local>
                  <ShoppingListItem
                    {item}
                    {registerProductRef}
                    onToggle={toggle}
                    onInput={handleProductInput}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  </div>
</main>
